import { Injectable } from '@angular/core';
import { Actions, ofType, Effect } from '@ngrx/effects';
import { Observable, of, empty, forkJoin } from 'rxjs';
import { catchError, switchMap, exhaustMap, map, tap, concatMap } from 'rxjs/operators';
import { Action } from '@ngrx/store/src/models';

import {
  TicketActionTypes,
  GetTicketListEntryPointSuccess,
  GetTicketListEntryPointError,
  GetChangeRecordsTicketSuccess,
  GetChangeRecordsTicketAssessmentSuccess,
  GetChangeRecordsTicketPlanningSuccess,
  GetChangeRecordsTicketSchedulingSuccess,
  GetChangeRecordsTicketAffectedSuccess,
  GetChangeRecordsTicketImplementationSuccess,
  GetChangeRecordsTicketApprovalsSuccess,
  GetChangeTasksForChangeRecordsTicketSuccess,
  GetPendingApprovalsSuccess,
  GetTicketSuccess,
  SearchTicketsSuccessAction,
  GetUpdatesAvailableSuccess,
  GetTicketApprovalsSuccess,
  SetOrderItems,

  GetRequestForChangesTicketSuccess,
  GetRequestForChangesTicketApprovalsSuccess,
  GetPendingLicensesSuccess,
  GetTicketLicensesSuccess,
} from '../../actions/ticket/ticket.actions';
import { TicketService } from '../../services/ticket/ticket.service';
import { TICKET_LIST_ENTRY_POINT } from 'src/constants/query-params';
import { ActivatedRoute } from '@angular/router';
import { Ticket } from 'src/app/shared/models/ticket/Ticket';
import { IResponseMessage } from 'src/app/shared/models/response-message/IResponseMessage';
import { NotificationService } from '../../services/notification/notification.service';
import { CUSTOMER_PORTAL_CONFIG } from '../../../../constants/local-storage-variables';
import { MAIN_TICKET_FEATURE } from '../../../../constants/configuration-variables';
import { TOPIC_TYPES} from './../../../../constants/local-storage-variables';

@Injectable()
export class TicketEffects {
  @Effect()
  public search(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.SEARCH, TicketActionTypes.CANCEL_SEARCH),
      switchMap((action: any) => {
        const body = {
          application: 'CPM',
          method: 'searchTopics',
          parameters: {
            filter: action.ticketFilters,
            paging: action.ticketPagingConfig
          }
        };
        return action.type === TicketActionTypes.CANCEL_SEARCH
          ? empty()
          : this.ticketService.post(body).pipe(
              map((tickets: any) => {
                return SearchTicketsSuccessAction({
                  tickets: tickets.results,
                  totalCount: tickets.totalcount
                });
              })
            );
      })
    );
  }

  @Effect()
  public getUpdatesAvailable(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_UPDATES_AVAILABLE),
      concatMap((action: any) => {
        const topicType = Object.values(JSON.parse(localStorage.getItem(TOPIC_TYPES))) || [];
        const topicFilter = [
          {
            field: 'creation_time',
            operator: 'within',
            value: 'yearLast'
          },
          {
            field: 'stateDetails',
            operator: 'equals',
            value: action.updatesType
          },
          {
            field: 'topic_type',
            operator: 'in',
            value: topicType
          }
        ];

        const cpConfig = JSON.parse(localStorage.getItem(CUSTOMER_PORTAL_CONFIG));
        if (cpConfig && cpConfig.hasOwnProperty(MAIN_TICKET_FEATURE) && cpConfig[MAIN_TICKET_FEATURE]) {
          topicFilter.push({
            field: 'is_main',
            operator: 'equals',
            value: 1
          });
        }

        const body = {
          application: 'CPM',
          method: 'searchTopics',
          parameters: {
            filter: topicFilter,
            paging: {
              dir: 'DESC',
              limit: 1,
              sort: 'last_modified_time',
              start: 0,
              type: 'wTotalCount'
            }
          }
        };
        return this.ticketService.post(body).pipe(
          map((tickets: any) => {
            return GetUpdatesAvailableSuccess({
              available: !!tickets.results && !!tickets.results.length,
              updatesType: action.updatesType
            });
          }),
          catchError(() => {
            return of(
              GetUpdatesAvailableSuccess({
                available: false,
                updatesType: action.updatesType
              })
            );
          })
        );
      })
    );
  }

  @Effect()
  public getTicketListEntryPoint(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_TICKET_LIST_ENTRY_POINT),
      exhaustMap(() => {
        return this.route.queryParams.pipe(
          map(params => {
            return GetTicketListEntryPointSuccess({
              [TICKET_LIST_ENTRY_POINT]: params[TICKET_LIST_ENTRY_POINT]
            });
          }),
          catchError((error: any) => {
            return of(GetTicketListEntryPointError({ error }));
          })
        );
      })
    );
  }

  @Effect()
  public getTicket(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_TICKET),
      tap(action => action),
      switchMap((action: any) => {
        return forkJoin(
          this.ticketService.post(this.getBodyForTicket(action.id)),
          this.ticketService.getTicketStatuses(action.topicType),
          this.ticketService.getTicketPrioritys()
        ).pipe(
          map(([ticket, statuses, prioritys]) => {
            return GetTicketSuccess({
              ticket,
              statuses,
              prioritys
            });
          }),
          catchError((error: IResponseMessage) => {
            const errorMsg = error.error ? error.error.errorMessage : error.message;
            // skip "SQLSTATE[23000]: Integrity constraint violation: 1062 Duplicate entry for key 'cpm_topic_views.PRIMARY'"
            if (errorMsg.indexOf('cpm_topic_views') === -1) {
              this.notificationService.showErrorNotification(errorMsg);
            }
            return empty();
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicket(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET),
      switchMap((action: any) => {
        return forkJoin(
          this.ticketService.post(this.getBodyForTicket(action.ticket.id)),
          this.ticketService.getTicketStatuses(action.topicType),
          this.ticketService.getTicketPrioritys()
        ).pipe(
          map(([ticket, statuses, prioritys]) => {
            return GetChangeRecordsTicketSuccess({
              ticket,
              statuses,
              prioritys
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketAssessment(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET_ASSESSMENT),
      exhaustMap((action: any) => {
        return this.ticketService.post(this.getBodyForTab(action.id, 'searchAssessments')).pipe(
          map((assessment: any) => {
            return GetChangeRecordsTicketAssessmentSuccess({
              assessment: assessment.results[0]
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketPlanning(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET_PLANNING),
      exhaustMap((action: any) => {
        return this.ticketService.post(this.getBodyForTab(action.id, 'searchPlans')).pipe(
          map((planning: any) => {
            return GetChangeRecordsTicketPlanningSuccess({
              planning: planning.results[0]
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketScheduling(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET_SCHEDULING),
      exhaustMap((action: any) => {
        return this.ticketService.post(this.getBodyForTab(action.id, 'searchSchedules')).pipe(
          map((scheduling: any) => {
            return GetChangeRecordsTicketSchedulingSuccess({
              scheduling: scheduling.results[0]
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketAffected(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET_AFFECTED),
      exhaustMap((action: any) => {
        const paging = {
          dir: action.dir,
          limit: 15,
          sort: action.searchFields,
          start: 0
        };
        return this.ticketService.post(this.getBodyForTab(action.id, 'searchAffectedCIs', paging)).pipe(
          map((affected: any) => {
            return GetChangeRecordsTicketAffectedSuccess({
              affected: affected.results
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketImplementation(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET_IMPLEMENTATION),
      exhaustMap((action: any) => {
        return this.ticketService.post(this.getBodyForTab(action.id, 'searchImplementations')).pipe(
          map((implementation: any) => {
            return GetChangeRecordsTicketImplementationSuccess({
              implementation: implementation.results[0]
            });
          })
        );
      })
    );
  }

  @Effect()
  public getTicketApprovals(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_TICKET_APPROVALS),
      exhaustMap((action: any) => {
        const paging = {
          dir: action.dir,
          limit: 15,
          sort: action.searchFields,
          start: 0
        };
        return this.ticketService.post(this.getBodyForApprovalTab(action.id, 'getUserApproval')).pipe(
          map((approvals: any) => {
            return GetTicketApprovalsSuccess({
              approvals: approvals.results[0]
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketApprovals(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_RECORDS_TICKET_APPROVALS),
      exhaustMap((action: any) => {
        const paging = {
          dir: action.dir,
          limit: 15,
          sort: action.searchFields,
          start: 0
        };
        return this.ticketService.post(this.getBodyForApprovalTab(action.id, 'getUserApproval')).pipe(
          map((approvals: any) => {
            return GetChangeRecordsTicketApprovalsSuccess({
              approvals: approvals.results[0]
            });
          })
        );
      })
    );
  }

  @Effect()
  public getChangeRecordTicketChangeTasks(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_CHANGE_TASKS_FOR_CHANGE_RECORDS_TICKET),
      exhaustMap((action: any) => {
        const body = {
          application: 'CPM',
          method: 'getTopic',
          parameters: {
            id: action.id,
            ignoreLock: true
          },
          paging: {
            dir: action.dir,
            limit: 15,
            sort: action.searchFields,
            start: 0
          }
        };
        return this.ticketService.post(body).pipe(
          map((changeTasks: any) => {
            return GetChangeTasksForChangeRecordsTicketSuccess({
              changeTasks
            });
          })
        );
      })
    );
  }

  @Effect()
  public getPendingApprovals(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_APPROVALS),
      exhaustMap((action: any) => {
        const body = {
          application: 'ChangeHub',
          method: 'getUserApproval',
          parameters: {}
        };
        return this.ticketService.post(body).pipe(
          map((approvals: any) => {
            return GetPendingApprovalsSuccess({
              approvals: approvals.results.length > 0 ? approvals.results[0].approvals.map(item => item) : [],
              totalCount: approvals.totalcount
            });
          })
        );
      })
    );
  }

  @Effect()
  public getOrderItems(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_ORDER_ITEMS),
      switchMap((action: any) => {
        return this.ticketService.post(this.getBodyForTab(action.id, 'searchOrders')).pipe(
          map((orders: any) => {
            return SetOrderItems({
              orders: orders.results.length > 0 ? orders.results[0].items || [] : []
            });
          })
        );
      })
    );
  }

  // RequestForChanges
  @Effect()
  public getRequestForChangeTicket(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_REQUEST_FOR_CHANGES_TICKET),
      switchMap((action: any) => {
        return forkJoin(
          this.ticketService.post(this.getBodyForTicket(action.ticket.id)),
          this.ticketService.getTicketStatuses(action.topicType),
          this.ticketService.getTicketPrioritys()
        ).pipe(
          map(([ticket, statuses, prioritys]) => {
            return GetRequestForChangesTicketSuccess({
              ticket,
              statuses,
              prioritys
            });
          })
        );
      })
    );
  }

  @Effect()
  public getRequestForChangesTicketApprovals(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_REQUEST_FOR_CHANGES_TICKET_APPROVALS),
      exhaustMap((action: any) => {
        return this.ticketService.post(this.getBodyForApprovalTab(action.id, 'getUserApproval')).pipe(
          map((approvals: any) => {
            return GetRequestForChangesTicketApprovalsSuccess({
              approvals: approvals.results[0]
            });
          })
        );
      })
    );
  }
  // end RequestForChanges

  private getBodyForTab(id: string, method: string, paging?) {
    return {
      application: 'ChangeHub',
      method,
      parameters: [
        [
          {
            field: 'record_model',
            operator: 'equals',
            value: 'CPM_Model_Topic'
          },
          { field: 'record_id', operator: 'equals', value: id }
        ],
        [{ limit: 1, start: 0 }]
      ],
      paging
    };
  }

  private getBodyForApprovalTab(id: string, method: string) {
    return {
      application: 'ChangeHub',
      method,
      parameters: {
          record_id: id
        }
    };
  }


  private getBodyForTicket(id) {
    return {
      application: 'CPM',
      method: 'getTopic',
      parameters: {
        id,
        ignoreLock: true
      }
    };
  }
// licenses
  @Effect()
  public getTicketLicenses(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_TICKET_LICENSES),
      exhaustMap((action: any) => {
        const paging = {
          dir: action.dir,
          limit: 15,
          sort: action.searchFields,
          start: 0
        };
        const body = {
          application: 'LicenseManagement',
          method: 'searchCustomerLicenses',
          parameters: {
            filter: [{
                field: 'topic_id',
                operator: 'equals',
                value: action.id
            }],
            paging: {
              dir: 'DESC',
              limit: 100,
              sort: 'id',
              start: 0,
              type: 'wTotalCount'
            }
          },
        };
        // this.getBodyForApprovalTab(action.id, 'getUserApproval')
        return this.ticketService.post(body).pipe(
          map((licenses: any) => {
            return GetTicketLicensesSuccess({
              licenses: licenses.results.map(item => item),
              totalCount: licenses.totalcount
            });
          })
        );
      })
    );
  }

  @Effect()
  public getPendingLicenses(): Observable<Action> {
    return this.actions.pipe(
      ofType(TicketActionTypes.GET_REQUEST_FOR_CHANGES_TICKET_LICENSES_SUCCESS),
      exhaustMap((action: any) => {
        const body = {
          application: 'LicenseManagement',
          method: 'searchCustomerLicenses',
          parameters: {
            filter: [{
                field: 'topic_id',
                operator: 'equals',
                value: action.id
            }],
            paging: {
              dir: 'DESC',
              limit: 100,
              sort: 'id',
              start: 0,
              type: 'wTotalCount'
            }
          },
        };
        return this.ticketService.post(body).pipe(
          map((licenses: any) => {
            return GetTicketLicensesSuccess({
              licenses: licenses.results.map(item => item),
              totalCount: licenses.totalcount
            });
          })
        );
      })
    );
  }
  constructor(
    private actions: Actions,
    private ticketService: TicketService,
    private notificationService: NotificationService,
    private route: ActivatedRoute
  ) {}
}
